import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import isEmpty from "lodash/isEmpty";

import Carousel from "wonderbly-components/lib/Carousel";
import Accordion, { AccordionItem } from "wonderbly-components/lib/Accordion";
import Review from "wonderbly-components/lib/Review";
import TagList from "wonderbly-components/lib/TagList";
import Price from "wonderbly-components/lib/Price";
import StarRating from "wonderbly-components/lib/StarRating";

import RichText from "../Prismic/RichText";
import Klarna from "./children/Klarna";
import ProductHeroIICta from "./children/ProductHeroIICta";
import ProductHeroIISlide from "./children/ProductHeroIISlide";
import PayPalMessage from "../Checkout/PayPal/PayPalMessage";
import TrustpilotWidget from "../TrustpilotWidget";

import { prismicImage } from "src/common/utils/prismic-image";

import "./ProductHeroII.scss";

const buildThumbnails = (items) => {
  return items.map((item) => prismicImage(item.image, { width: 120 }));
};

const ProductHeroII = (props) => {
  return (
    <section className="ProductHeroII">
      <header className="ProductHeroII__header">
        <h1 className="ProductHeroII__title">{props.productTitle}</h1>
        <div
          className={cx("ProductHeroII__subtitle-wrap", {
            "ProductHeroII__subtitle-wrap--inline":
              !props.productRatingTpOverride,
          })}
        >
          {props.model.prismicProduct.productInformation
            .productBadgeContent && (
            <p className="ProductHeroII__subtitle">
              {
                props.model.prismicProduct.productInformation
                  .productBadgeContent
              }
            </p>
          )}
          <div className="ProductHeroII__star-rating">
            {props.productRatingTpOverride ? (
              <div className="ProductHeroII__widget">
                Trustpilot
                <TrustpilotWidget
                  templateId="productmini"
                  productId={props.trustPilotSkus}
                  isComponent={false}
                  className="ProductHeroII__tpwidget"
                />
              </div>
            ) : (
              <StarRating
                stars={props.productRating}
                trustPilot={props.productRatingTheme === "trustpilot"}
              />
            )}
          </div>
        </div>
      </header>
      <div className="ProductHeroII__carousel">
        <Carousel
          loop
          showControls
          controlsColor={props.invertCarousel ? "light" : "dark"}
          paginationColor={props.invertCarousel ? "light" : "dark"}
          showThumbs="left"
          showPagination="inside"
          thumbsClassname="ProductHeroII__carousel-thumbs"
          thumbs={buildThumbnails(props.items)}
        >
          {props.items.map((slide, idx) => (
            <ProductHeroIISlide
              key={idx}
              index={idx}
              invert={props.invertCarousel}
              {...slide}
            />
          ))}
        </Carousel>
      </div>
      {props.productDescription && (
        <div className="ProductHeroII__body">
          <RichText fields={props.productDescription} />
        </div>
      )}
      {!isEmpty(props.productTags) && (
        <div className="ProductHeroII__tags">
          <TagList tags={props.productTags} />
        </div>
      )}
      {props.displayPrice && props.price && (
        <div className="ProductHeroII__price">
          <Price
            large
            was={props.savings?.was_price?.formatted}
            is={props.price?.formatted}
          />
        </div>
      )}
      {props.displayCtaButton && (
        <ProductHeroIICta
          prismicProduct={props.model.prismicProduct}
          productSlug={props.model.eagleProduct.slug}
          productCtaButtonText={props.productCtaButtonText}
          productCreationFlow={props.productCreationFlow}
          productAvatarCreation={props.productAvatarCreation}
        />
      )}
      {props.globalContent?.checkout.payLaterMessage && (
        <div className="ProductHeroII__paypal">
          <PayPalMessage />
        </div>
      )}
      {props.globalContent?.general?.klarnaMessaging && (
        <div className="ProductHeroII__klarna">
          <Klarna text={props.globalContent.general.klarnaMessaging} />
        </div>
      )}
      {!isEmpty(props.productAccordion) && (
        <div className="ProductHeroII__accordion">
          <Accordion closeOtherItems>
            {props.productAccordion.map((item, idx) => (
              <AccordionItem key={idx} id={idx} header={<h3>{item.title}</h3>}>
                <RichText fields={item.content} />
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
      {props.productReview && (
        <div className="ProductHeroII__review">
          <Review
            title={
              props.productRatingTpOverride ? (
                <div className="ProductHeroII__widget ProductHeroII__widget--is-stacked-mobile">
                  Trustpilot
                  <TrustpilotWidget
                    templateId="productmini"
                    productId={props.trustPilotSkus}
                    isComponent={false}
                    className="ProductHeroII__tpwidget"
                  />
                </div>
              ) : (
                props.productRatingText
              )
            }
            review={props.productReview}
            rating={props.productRatingTpOverride ? null : props.productRating}
            ratingTheme={props.productRatingTheme}
            cta={{
              onClick: props.handleReviewCtaClick,
              href: props.productReviewLinkHref,
              text: props.productReviewLinkText,
            }}
          />
        </div>
      )}
    </section>
  );
};

ProductHeroII.propTypes = {
  globalContent: PropTypes.object,
  country: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired,
  productId: PropTypes.string.isRequired,
  trustPilotSkus: PropTypes.string.isRequired,
  productTitle: PropTypes.string.isRequired,
  model: PropTypes.object.isRequired,
  price: PropTypes.object,
  displayPrice: PropTypes.bool,
  displayCtaButton: PropTypes.bool,
  invertCarousel: PropTypes.bool,
  savings: PropTypes.object,
  handleCtaClick: PropTypes.func,
  handleReviewCtaClick: PropTypes.func,
  productCtaButtonText: PropTypes.string,
  productCtaButtonHref: PropTypes.string,
  productDescription: PropTypes.array,
  productReview: PropTypes.array,
  productTags: PropTypes.array,
  productRatingTpOverride: PropTypes.bool,
  productRating: PropTypes.number,
  productReview: PropTypes.string,
  productRatingText: PropTypes.string,
  productRatingTheme: PropTypes.oneOf(["default", "trustpilot"]),
  productReviewLinkText: PropTypes.string,
  productReviewLinkHref: PropTypes.string,
  productAccordion: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.array,
    })
  ),
  productAvatarCreation: PropTypes.object,
  productCreationFlow: PropTypes.object,
  product: PropTypes.object,
  prismicProduct: PropTypes.object,
};

export default ProductHeroII;
